import React, { useState } from 'react';
import { useTranslation } from '@wix/cli-i18n';
import { useGenerationTask } from '../hooks/use-generation-task.js';
import { useProjectModel } from '../../../model.js';
import { generateApi, generateWebMethod } from './generate.js';
import {
  CompleteGeneration,
  CompleteGenerationMessage,
} from '../CompleteGeneration.js';
import {
  ApiFolderStep,
  SelectApiTypeStep,
  WebMethodFolderStep,
} from './steps.js';
import type { DocsExtensionType } from '../docsExtensionType.js';

export type ApiType = 'HttpFunction' | 'WebMethod';

export const useGenerateApi = (docsExtensionType?: DocsExtensionType) => {
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const [selectedDocsExtensionType, setSelectedDocsExtensionType] = useState<
    DocsExtensionType | undefined
  >(() => docsExtensionType);
  const [apiType, setApiType] = useState<ApiType>();
  const [apiFolder, setApiFolder] = useState<string>();

  const extensionType =
    apiType === 'HttpFunction'
      ? t('generate_command.flow.api.title').toLowerCase()
      : t('generate_command.api.web_method.title').toLowerCase();

  const generationTask = useGenerationTask(async () => {
    if (apiFolder) {
      if (apiType === 'HttpFunction') {
        return generateApi(model.projectFolder, apiFolder);
      }

      if (apiType === 'WebMethod') {
        return generateWebMethod(model.projectFolder, apiFolder);
      }
    }
  }, [model.projectFolder, apiFolder]);

  return [
    <SelectApiTypeStep
      docsExtensionType={selectedDocsExtensionType}
      onSubmit={(value) => {
        setApiType(value);
        // I need to reset `selectedDocsExtensionType` on submission so if the user goes back in history (using Esc),
        // they'll be able to select the API type anew.
        setSelectedDocsExtensionType(undefined);
      }}
    />,
    apiType === 'HttpFunction' ? (
      <ApiFolderStep onSubmit={setApiFolder} />
    ) : (
      <WebMethodFolderStep onSubmit={setApiFolder} />
    ),
    <CompleteGeneration
      generationTask={generationTask}
      extensionType={extensionType}
      successMessage={CompleteGenerationMessage.SuccessMessage({
        learnMoreUrl:
          apiType === 'HttpFunction'
            ? // https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/backend-extensions/api/add-api-extensions-with-the-cli
              'https://wix.to/3wEnGrE'
            : // https://dev.wix.com/docs/build-apps/develop-your-app/frameworks/wix-cli/supported-extensions/backend-extensions/web-methods/add-web-method-extensions-with-the-cli
              'https://wix.to/hjf9SQX',
      })}
      route={apiFolder}
    />,
  ];
};
